import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Pencil, Send, Trash2, CheckCircle2, XCircle, MessageSquare, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import DocumentsPanel from "@/components/DocumentsPanel";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLE, OUTSIDE_PURCHASE_ROLES, roleLabel } from "@/constants/roles";
import { paymentMethodLabel, expenseStatus, expenseIsOpen } from "@/constants/outsidePurchases";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import { qboTransactionLink } from "@/lib/qbo";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

const Info = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
    <div className="mt-0.5 text-[13.5px]">{children || "—"}</div>
  </div>
);

export default function ExpenseLogDetail() {
  const { id } = useParams();
  const [op, setOp] = useState(null);
  const [qboEnvironment, setQboEnvironment] = useState(null);
  const [costCodes, setCostCodes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [decision, setDecision] = useState(null); // needs_info | reject
  const [message, setMessage] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get(`/contractor/outside-purchases/${id}`).then((r) => setOp(r.data));
  useEffect(() => {
    load();
    api.get("/contractor/cost-codes", { params: { include_inactive: true } }).then((r) => setCostCodes(r.data));
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sandbox vs production QuickBooks host, only once there's something to open.
  useEffect(() => {
    if (op?.qbo_sync?.qbo_id && qboEnvironment === null) {
      api.get("/contractor/accounting/qbo-environment").then((r) => setQboEnvironment(r.data.environment)).catch(() => {});
    }
  }, [op?.qbo_sync?.qbo_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!op) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const canManage = OUTSIDE_PURCHASE_ROLES.includes(user?.role);
  const status = expenseStatus(op);
  const open = expenseIsOpen(op);
  const canDelete = open && (user?.role === ADMIN_ROLE || op.purchased_by === user?.id);
  const costCodeLabel = (ccId) => {
    const cc = costCodes.find((c) => c.id === ccId);
    return cc ? `${cc.code} — ${cc.name}` : null;
  };
  const job = jobs.find((j) => j.id === (op.job_id || op.context?.job_id));
  const jobLabel = job ? `${job.code} — ${job.name}` : op.context?.type ? op.context.type.replace(/_/g, " ") : null;
  const lines = (op.lines || []).filter((l) => !l.is_tax);
  const headerCostCode = op.cost_code_id || (lines.length === 1 ? lines[0].cost_code_id : null);

  const act = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      setDecision(null);
      setMessage("");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((d) => d.message).join(" ") : detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };
  const submit = () => act(() => api.patch(`/contractor/outside-purchases/${id}`, { submit: true }), status === "draft" ? "Submitted for approval" : "Resubmitted for approval");
  const approve = () => act(() => api.post(`/contractor/outside-purchases/${id}/approve`, {}), "Expense approved");
  const decide = () => act(
    () => api.post(`/contractor/outside-purchases/${id}/${decision === "reject" ? "reject" : "needs-info"}`, { message }),
    decision === "reject" ? "Expense rejected" : "Sent back to the purchaser",
  );
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/contractor/outside-purchases/${id}`);
      toast.success(`${op.po_number || op.op_number} deleted`);
      navigate("/contractor/expense-log");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const approvalLine = () => {
    if (status === "approved") return <span className="text-emerald-700">Approved by <b>{op.approved_by_name}</b>{op.approved_at ? ` on ${fmtDate(op.approved_at)}` : ""}</span>;
    if (status === "rejected") return <span className="text-red-700">Rejected</span>;
    if (status === "pending_approval") return <span className="text-amber-700">Not approved yet — waiting on {op.awaiting_role ? roleLabel(op.awaiting_role) : "an approver"}</span>;
    if (status === "needs_info") return <span className="text-amber-700">Not approved — more info requested</span>;
    return <span className="text-slate-500">Not submitted</span>;
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="expense-detail-page">
      <Link to="/contractor/expense-log" className="text-[12.5px] text-slate-500 flex items-center gap-1 mb-3"><ChevronLeft size={13} /> Back to Expense Log</Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-extrabold tracking-tight mono">{op.po_number || op.op_number}</h1>
            <StatusBadge status={status} />
          </div>
          <p className="text-[13px] mt-1" data-testid="expense-approval-line">{approvalLine()}</p>
        </div>
        {open && canManage && (
          <div className="flex flex-wrap gap-2">
            {canDelete && (
              <button onClick={() => setConfirmDelete(true)} data-testid="delete-expense-button" className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold text-red-600 hover:bg-red-50">
                <Trash2 size={14} /> Delete
              </button>
            )}
            <button onClick={() => navigate(`/contractor/expense-log/${id}/edit`)} data-testid="modify-expense-button" className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50">
              <Pencil size={14} /> Modify
            </button>
            {(status === "draft" || status === "needs_info" || status === "rejected") && (
              <button disabled={busy} onClick={submit} data-testid="submit-expense-button" className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold disabled:opacity-60">
                <Send size={14} /> {status === "draft" ? "Submit for Approval" : "Resubmit"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Same as a vendor invoice: once synced, open the Expense (or Bill) in QuickBooks. */}
      {op.qbo_sync?.qbo_id && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-100 px-3.5 py-2.5 text-[12.5px]" data-testid="expense-qbo-sync-banner">
          <span className="text-slate-600">
            Synced to QuickBooks as {op.qbo_sync.qbo_type === "Purchase" ? "Expense" : op.qbo_sync.qbo_type} #{op.qbo_sync.qbo_id}
            {op.qbo_sync.at ? ` · ${fmtDateTime(op.qbo_sync.at)}` : ""}
          </span>
          {qboEnvironment && (
            <a href={qboTransactionLink(qboEnvironment, op.qbo_sync.qbo_type, op.qbo_sync.qbo_id)} target="_blank" rel="noopener noreferrer"
              data-testid="view-expense-in-quickbooks-link" className="flex items-center gap-1 text-sky-600 font-medium hover:underline shrink-0">
              View in QuickBooks <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}
      {op.sync_error && op.accounting_status === "ready_for_sync" && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-100 px-3.5 py-2.5 text-[12.5px] text-rose-800" data-testid="expense-qbo-sync-error">
          QuickBooks sync failed: {op.sync_error.message}
        </div>
      )}

      {op.can_approve && (
        <div className="mt-5 rounded-xl border border-sky-200 bg-sky-50 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="expense-approval-panel">
          <div className="text-[13px] text-sky-900"><b>{op.purchased_by_name}</b> is waiting on your approval for {fmt(op.total)}.</div>
          <div className="flex gap-2 flex-wrap">
            <button disabled={busy} onClick={() => { setDecision("reject"); setMessage(""); }} data-testid="reject-expense-button" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold text-red-600 hover:bg-red-50"><XCircle size={13} /> Reject</button>
            <button disabled={busy} onClick={() => { setDecision("needs_info"); setMessage(""); }} data-testid="needs-info-expense-button" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold hover:bg-slate-50"><MessageSquare size={13} /> Needs info</button>
            <button disabled={busy} onClick={approve} data-testid="approve-expense-button" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold disabled:opacity-60"><CheckCircle2 size={13} /> Approve</button>
          </div>
        </div>
      )}

      {op.approval_message && (status === "needs_info" || status === "rejected") && (
        <div className={`mt-4 text-[12.5px] rounded-lg px-3.5 py-3 border ${status === "rejected" ? "text-red-900 bg-red-50 border-red-100" : "text-amber-900 bg-amber-50 border-amber-100"}`} data-testid="expense-approval-message">
          <div className={`font-semibold text-[11px] uppercase tracking-wide mb-0.5 ${status === "rejected" ? "text-red-600" : "text-amber-600"}`}>{status === "rejected" ? "Reason for rejection" : "More info requested"}</div>
          {op.approval_message}
          <div className={`mt-1 ${status === "rejected" ? "text-red-700" : "text-amber-700"}`}>Click Modify to fix the entry, then Resubmit{status === "rejected" ? " — or delete it" : ""}.</div>
        </div>
      )}

      <div className="mt-6 border border-slate-200 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="expense-fields">
        <Info label="Purchaser">{op.purchased_by_name}</Info>
        <Info label="Date">{op.purchase_date ? fmtDate(op.purchase_date) : null}</Info>
        <Info label="Vendor">{op.vendor_name_freetext}</Info>
        <Info label="Vendor invoice #">{op.vendor_invoice_number}</Info>
        <Info label="Job">{jobLabel}</Info>
        <Info label="Card or Accounts Payable">{op.payment_method ? `${paymentMethodLabel(op.payment_method)}${op.card_last4 ? ` ···· ${op.card_last4}` : ""}` : null}</Info>
        <Info label="Quoted amount">{op.quoted_amount != null ? fmt(op.quoted_amount) : null}</Info>
        <Info label={op.tax_amount ? "Actual amount (with tax)" : "Actual amount"}><span className="mono font-bold">{fmt(op.total)}</span></Info>
        {op.taxable && <Info label="Tax" testId="expense-tax-info">{op.tax_amount ? `${fmt(op.tax_amount)} at ${op.tax_rate}%` : "Taxable — job has no tax rate yet"}</Info>}
        <Info label="Cost account">{headerCostCode ? costCodeLabel(headerCostCode) : lines.length > 1 ? "Split — see lines" : null}</Info>
        <div className="col-span-2 sm:col-span-3"><Info label="Memo">{op.memo}</Info></div>
      </div>

      {op.admin_approval_note && (
        <div className="mt-4 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3">
          <div className="font-semibold text-[11px] uppercase tracking-wide text-amber-600 mb-0.5">Admin approval note (no receipt)</div>
          {op.admin_approval_note}
        </div>
      )}

      {lines.length > 1 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="expense-lines-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2">Description</th><th className="text-left py-2">Cost account</th><th className="text-right py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2.5">{l.description}</td>
                  <td className="py-2.5 text-slate-500">{costCodeLabel(l.cost_code_id) || <span className="text-amber-600">Needs cost account</span>}</td>
                  <td className="py-2.5 text-right mono font-medium">{fmt(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(op.approval_chain || []).some((s) => s.acted_at) && (
        <div className="mt-6 text-[12.5px] text-slate-500 space-y-1" data-testid="expense-approval-history">
          {op.approval_chain.filter((s) => s.acted_at).map((s, i) => (
            <div key={i}>{s.status === "approved" ? "Approved" : "Rejected"} by {s.acted_by_name || roleLabel(s.acted_by_role || s.approver_role)} · {fmtDateTime(s.acted_at)}{s.comment ? ` — ${s.comment}` : ""}</div>
          ))}
        </div>
      )}

      <DocumentsPanel entityType="outside_purchase" entityId={op.id} title="Attachments" defaultDocType="receipt" readOnly={!open || !canManage} />

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="expense-decision-dialog">
          <DialogHeader><DialogTitle>{decision === "reject" ? "Reject this expense" : "Ask for more info"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} autoFocus data-testid="expense-decision-message"
              placeholder={decision === "reject" ? "Why is it rejected?" : "What's missing or needs fixing?"}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-sky-400" />
            <button disabled={busy || !message.trim()} onClick={decide} data-testid="confirm-expense-decision" className={`w-full h-10 rounded-lg text-white text-[13px] font-semibold disabled:opacity-60 ${decision === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-sky-500 hover:bg-sky-600"}`}>
              {decision === "reject" ? "Reject" : "Send back to purchaser"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {op.po_number || op.op_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the entry and its attachments. The audit trail keeps a record that it was deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); remove(); }} data-testid="confirm-delete-expense-button" className="bg-red-600 hover:bg-red-700">
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
